import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { isAdminUser } from "../utils/security";

const getRecoveryParams = () => {
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const searchParams = new URLSearchParams(window.location.search);

  return {
    accessToken: hashParams.get("access_token") || searchParams.get("access_token"),
    refreshToken: hashParams.get("refresh_token") || searchParams.get("refresh_token"),
    code: searchParams.get("code"),
  };
};

function ProtectedAdminRoute() {
  const location = useLocation();
  const [checkingSession, setCheckingSession] = useState(true);
  const [isAuthorized, setIsAuthorized] = useState(false);

  useEffect(() => {
    let mounted = true;

    const verifyAdminSession = async () => {
      try {
        let {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          throw error;
        }

        if (!session && location.pathname === "/update-password") {
          const { accessToken, refreshToken, code } = getRecoveryParams();

          if (accessToken && refreshToken) {
            const { data, error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });

            if (sessionError) {
              throw sessionError;
            }

            session = data.session;
          } else if (code) {
            const { data, error: codeError } = await supabase.auth.exchangeCodeForSession(code);

            if (codeError) {
              throw codeError;
            }

            session = data.session;
          }
        }

        const authorized = isAdminUser(session?.user);

        if (session && !authorized) {
          await supabase.auth.signOut();
        }

        if (mounted) {
          setIsAuthorized(authorized);
        }
      } catch (sessionError) {
        console.error("Supabase admin session verification error:", sessionError);

        if (mounted) {
          setIsAuthorized(false);
        }
      } finally {
        if (mounted) {
          setCheckingSession(false);
        }
      }
    };

    verifyAdminSession();

    return () => {
      mounted = false;
    };
  }, [location.pathname]);

  if (checkingSession) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-black px-6 text-white">
        <p className="rounded-xl bg-white/[0.04] px-4 py-3 text-sm font-semibold text-neutral-300">
          Checking admin session...
        </p>
      </main>
    );
  }

  if (!isAuthorized) {
    return <Navigate to="/admin-login" replace state={{ from: location }} />;
  }

  return <Outlet />;
}

export default ProtectedAdminRoute;
